/**
 * 1.6.0 AI assistant access: the data hooks (docs/dev/mcp-1.6.0-build.md). Step 0 wrote them against T's routes (their
 * stubs answer the access as off and empty lists; changes 501); stream T owns this file now and fixes a hook when its
 * route turns out different.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminUserTokenDTO, AssistantSettingsDTO, AssistantSettingsInput, CreateUserTokenInput, CreatedUserTokenDTO, MyAssistantAccessDTO, UpdateUserTokenInput, UserTokenDTO,
} from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import type { McpDownloadFile, McpDownloads } from '../../lib/mcp-install';

/** Whether My account shows "AI assistant access", and what the person may do there. */
export function useMyAssistantAccess(enabled = true) {
  return useQuery({ queryKey: queryKeys.myAssistantAccess(), queryFn: () => api<MyAssistantAccessDTO>('/api/v1/me/assistant-access'), enabled, staleTime: 60_000 });
}

export function useMyTokens(enabled = true) {
  return useQuery({ queryKey: queryKeys.myTokens(), queryFn: () => api<{ items: UserTokenDTO[] }>('/api/v1/me/tokens'), enabled });
}

export function useCreateToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CreateUserTokenInput> & { name: string }) => api<CreatedUserTokenDTO>('/api/v1/me/tokens', { method: 'POST', json: body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.myTokens() }); },
  });
}

export function useUpdateToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateUserTokenInput & { id: string }) => api<UserTokenDTO>(`/api/v1/me/tokens/${id}`, { method: 'PATCH', json: body }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.myTokens() }); },
  });
}

export function useRevokeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/v1/me/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: queryKeys.myTokens() }); },
  });
}

/** Access → AI assistants (admin). */
export function useAssistantSettings(enabled = true) {
  return useQuery({ queryKey: queryKeys.assistantSettings(), queryFn: () => api<AssistantSettingsDTO>('/api/v1/settings/assistants'), enabled });
}

export function useSaveAssistantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssistantSettingsInput) => api<AssistantSettingsDTO>('/api/v1/settings/assistants', { method: 'PUT', json: body }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.assistantSettings(), data);
      void qc.invalidateQueries({ queryKey: queryKeys.myAssistantAccess() });
    },
  });
}

export function useAdminTokens(state: 'live' | 'all' = 'live', enabled = true) {
  return useQuery({ queryKey: queryKeys.adminTokens(state), queryFn: () => api<{ items: AdminUserTokenDTO[] }>(`/api/v1/admin/user-tokens?state=${state}`), enabled });
}

export function useAdminRevokeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/user-tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-tokens'] });
      void qc.invalidateQueries({ queryKey: queryKeys.assistantSettings() });
      void qc.invalidateQueries({ queryKey: queryKeys.myTokens() });
    },
  });
}

/**
 * The MCP connector programs this PRISM serves (/downloads/mcp/latest.json and its manifest, stream M's files, as the
 * storage agent's); null when it has none (a development server without the builds, or an image built without them).
 */
export function useMcpDownloads(enabled = true) {
  return useQuery({
    queryKey: queryKeys.mcpDownloads(),
    staleTime: 60_000,
    retry: false,
    enabled,
    queryFn: async (): Promise<McpDownloads | null> => {
      try {
        const latest = await fetch('/downloads/mcp/latest.json', { cache: 'no-cache' });
        if (!latest.ok) return null;
        const { version, path } = (await latest.json()) as { version: string; path: string };
        const base = `${window.location.origin}${path.replace(/\/$/, '')}`;
        const m = await fetch(`${base}/manifest.json`, { cache: 'no-cache' });
        if (!m.ok) return null;
        const manifest = (await m.json()) as { files: McpDownloadFile[] };
        return manifest.files?.length ? { version, base, files: manifest.files } : null;
      } catch {
        return null; // not JSON (the app's index page), or unreachable
      }
    },
  });
}
